import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import { injectIntl, IntlShape } from 'react-intl';
import moment from 'moment';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import {
  extractStatusExpert,
  renderNatureMission,
} from 'containers/ExpertAssignedMissions/utils';
import messages from 'containers/ExpertAssignedMissions/messages';
import { translate } from 'containers/services/translate';
import {
  Container,
  Header,
  Title,
  StyledTableHead,
  StyledTablePagination,
  StyledTableRow,
} from 'containers/ExpertAssignedMissions/style';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import ActionDropDown from 'components/ActionDropDown/index';
import { getValueByCode as getValueByCodeSagis } from 'containers/CourtManagerLegalProceedings/Provision/utils';
import { getValueByCode } from 'containers/common/LegalProceedings/JudgementAdd/Recap/utils';
import { LISTINGS } from 'containers/data-structure';
import { Config } from 'containers/AppContainer/typings';
import {
  PV_DE_CARENCE_STATUS,
  FINISHED_STATUS,
} from 'containers/ExpertHomePage/constants';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';

export interface ResultSearchTableProps {
  intl: IntlShape;
  missions: any;
  missionStatusIds: Config[];
}

const ResultSearchTable: FC<ResultSearchTableProps> = ({
  missions,
  missionStatusIds,
  ...props
}) => {
  const listings = useSelector((state: any) =>
    get(state.toJS(), ['appContainer', 'listings'], []),
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* istanbul ignore next */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tableHeaderTitles = featureService.hasFeature(FEATURES.TRANSACTION)
    ? [
        translate(messages.claimNumber, props),
        translate(messages.missionNumber, props),
        translate(messages.missionDate, props),
        translate(messages.concernedParty, props),
        translate(messages.city, props),
        translate(messages.lawyerAdvice, props),
        translate(messages.missionStatus, props),
        translate(messages.nature, props),
        '',
      ]
    : [
        translate(messages.claimNumber, props),
        translate(messages.missionNumber, props),
        translate(messages.missionDate, props),
        translate(messages.concernedParty, props),
        translate(messages.city, props),
        translate(messages.lawyerAdvice, props),
        translate(messages.missionStatus, props),
        '',
      ];

  const missionRows: any[] = missions.map((mission: any) => ({
    missionNumber: mission?.reference,
    assignedExpert: mission?.assignedExpert,
    assignedExperts: mission?.assignedExperts,
    courtFileNumber: mission?.courtFileNumber,
    type: mission?.type,
    missionDate: mission?.missionDate,
    claimNumber: mission?.claim?.claimNumber,
    city: getValueByCode(
      mission?.city,
      get(listings, LISTINGS.JURISDICTION, []),
    ),
    address: mission?.address,
    document: mission?.document,
    lossHour: mission?.claim?.lossHour,
    procedureNatureId: mission?.procedureNatureId,
    stepId: mission?.step,
    legalExpert: mission?.legalExpert,
    lawyer: getValueByCodeSagis(mission?.lawyer, get(listings, 'intervenants')),
    victim:
      get(mission?.victims, '[0].firstName', ' ') +
      ' ' +
      get(mission?.victims, '[0].lastName', ' '),
    vehicle: get(mission?.vehicles, '[0].registrationNumber', ' '),
    claimId: mission?.claim?.id,
    vehicles: mission?.vehicles,
    id: mission?.id,
    nature: mission?.nature,
    victims: mission?.victims,
  }));
  return (
    <>
      <Container>
        <Header>
          <Title>{translate(messages.searchResultTitle, props)}</Title>
        </Header>
        <TableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                {tableHeaderTitles.map(title => (
                  <TableCell key={title}>{title}</TableCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {missionRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any) => {
                  return (
                    <StyledTableRow
                      data-testid="mission-row"
                      key={`${row.missionNumber}${row.victim}`}
                      onClick={() => {
                        pushTo(
                          `${PAGES.EXPERT_ASSIGNED_MISSION_SHOW}/${row.id}`,
                        )();
                      }}
                    >
                      <TableCell>{row.claimNumber}</TableCell>
                      <TableCell>{row.missionNumber}</TableCell>
                      <TableCell>
                        {moment(row.missionDate).format('DD/MM/yyyy') ===
                        'Invalid date'
                          ? '-'
                          : moment(row.missionDate).format('DD/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        {row.victim ? row.victim : row.vehicle}
                      </TableCell>
                      <TableCell>{row.city}</TableCell>
                      <TableCell>{row.lawyer}</TableCell>
                      <TableCell>
                        {getValueByCode(
                          extractStatusExpert(
                            row,
                            currentIntervenantStorage.get()?.sagis_code,
                          ),
                          missionStatusIds,
                        )}
                      </TableCell>
                      {featureService.hasFeature(FEATURES.TRANSACTION) && (
                        <TableCell>
                          {translate(
                            renderNatureMission(get(row, 'nature')),
                            props,
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <ActionDropDown
                          label={translate(messages.action, props)}
                          items={
                            extractStatusExpert(
                              row,
                              currentIntervenantStorage.get()?.sagis_code,
                            ) === PV_DE_CARENCE_STATUS ||
                            extractStatusExpert(
                              row,
                              currentIntervenantStorage.get()?.sagis_code,
                            ) === FINISHED_STATUS
                              ? [
                                  {
                                    title: translate(messages.consult, props),
                                    onClick: pushTo(
                                      `${PAGES.EXPERT_ASSIGNED_MISSION_SHOW}/${row.id}?tab=0`,
                                    ),
                                  },
                                ]
                              : [
                                  {
                                    title: translate(messages.consult, props),
                                    onClick: pushTo(
                                      `${PAGES.EXPERT_ASSIGNED_MISSION_SHOW}/${row.id}?tab=0`,
                                    ),
                                  },
                                  {
                                    title: translate(messages.treat, props),
                                    onClick: pushTo(
                                      `${PAGES.EXPERT_ASSIGNED_MISSION_SHOW}/${row.id}?tab=1`,
                                    ),
                                  },
                                ]
                          }
                        />
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <StyledTablePagination
          rowsPerPageOptions={[5, 10, 25]}
          count={missionRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage={translate(messages.rowsPerPage, props)}
        />
      </Container>
    </>
  );
};

export default injectIntl(ResultSearchTable);
